import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  url?: string;
  image?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = 'บริษัท ไทย วายเอสวาย เทรดดิ้ง จำกัด | โน้ตบุค อุปกรณ์คอมพิวเตอร์ เครื่องเสียง',
  description = 'บริษัท ไทย วายเอสวาย เทรดดิ้ง จำกัด - ผู้เชี่ยวชาญด้านการจัดจำหน่ายโน้ตบุค อุปกรณ์คอมพิวเตอร์ และเครื่องเสียงคุณภาพสูง พร้อมบริการหลังการขายที่ดีเยี่ยม',
  keywords = 'โน้ตบุค, อุปกรณ์คอมพิวเตอร์, เครื่องเสียง, หูฟังบลูทูธ, ลำโพงบลูทูธ, ไมโครโฟน, ลำโพง, เครื่องขยายเสียง, บริษัท ไทย วายเอสวาย เทรดดิ้ง, THAI YSY TRADING',
  url = 'https://www.thaiysy.info',
  image = 'https://images.unsplash.com/photo-1550745165-9bc0b252726a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
}) => {
  useEffect(() => {
    document.title = title;
    
    // Update or create meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };
    
    const updatePropertyTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };
    
    // Basic Meta Tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', 'บริษัท ไทย วายเอสวาย เทรดดิ้ง จำกัด');
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('language', 'Thai');
    updateMetaTag('revisit-after', '7 days');
    
    // Open Graph / Facebook
    updatePropertyTag('og:type', 'website');
    updatePropertyTag('og:url', url);
    updatePropertyTag('og:title', title);
    updatePropertyTag('og:description', description);
    updatePropertyTag('og:image', image);
    updatePropertyTag('og:site_name', 'บริษัท ไทย วายเอสวาย เทรดดิ้ง จำกัด');
    updatePropertyTag('og:locale', 'th_TH');
    
    // Twitter
    updatePropertyTag('twitter:card', 'summary_large_image');
    updatePropertyTag('twitter:url', url);
    updatePropertyTag('twitter:title', title);
    updatePropertyTag('twitter:description', description);
    updatePropertyTag('twitter:image', image);
    
    // Additional SEO
    updateMetaTag('theme-color', '#2563eb');
    updateMetaTag('msapplication-TileColor', '#2563eb');
    updateMetaTag('application-name', 'THAI YSY TRADING');
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = url;
    
    // Structured Data
    let structuredData = document.querySelector('script[type="application/ld+json"]');
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.setAttribute('type', 'application/ld+json');
      document.head.appendChild(structuredData);
    }
    structuredData.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "บริษัท ไทย วายเอสวาย เทรดดิ้ง จำกัด",
      "alternateName": "THAI YSY TRADING CO., LTD.",
      "url": url,
      "logo": "https://www.thaiysy.info/logo.png",
      "image": "https://images.unsplash.com/photo-1550745165-9bc0b252726a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      "telephone": "02-123-4567",
      "email": "info@thaiysy.info",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "32/38 หมู่บ้าน มัณฑนา บางนา วงแหวน ถนนรามคำแหง 2 แขวงดอกไม้",
        "addressLocality": "เขตประเวศ",
        "addressRegion": "กรุงเทพมหานคร",
        "postalCode": "10250",
        "addressCountry": "TH"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "13.6769",
        "longitude": "100.6353"
      },
      "openingHours": [
        "Mo-Fr 08:00-17:00",
        "Sa 09:00-16:00",
        "Su Closed"
      ],
      "priceRange": "$$",
      "paymentAccepted": ["Cash", "Credit Card", "Bank Transfer"],
      "currenciesAccepted": "THB",
      "sameAs": [
        "https://www.facebook.com/thaiysytrading",
        "https://www.instagram.com/thaiysytrading",
        "https://www.line.me/ti/p/~@thaiysytrading"
      ],
      "description": description,
      "foundingDate": "2023-11-22",
      "legalName": "บริษัท ไทย วายเอสวาย เทรดดิ้ง จำกัด",
      "taxID": "0105566223035",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "สินค้าทั้งหมด",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "โน้ตบุค",
              "category": "คอมพิวเตอร์"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "อุปกรณ์คอมพิวเตอร์",
              "category": "อุปกรณ์อิเล็กทรอนิกส์"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "เครื่องเสียง",
              "category": "อุปกรณ์เสียง"
            }
          }
        ]
      },
      "areaServed": {
        "@type": "Country",
        "name": "Thailand"
      },
      "knowsLanguage": ["Thai", "English"],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "02-123-4567",
        "contactType": "customer service",
        "availableLanguage": ["Thai", "English"],
        "hoursAvailable": [
          "Mo-Fr 08:00-17:00",
          "Sa 09:00-16:00"
        ]
      }
    });
    
    // Set HTML lang attribute
    document.documentElement.lang = 'th';
  }, [title, description, keywords, url, image]);

  return null;
};

export default SEO;